import i18n, { ThirdPartyModule, TOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { BrandliveEvent, FeatureFlagMap, FeatureFlagsEnum } from "../types/working-model";
import store from '../store/main';
import { setTranslationUpdates } from "../store/actions/event/event-actions";
import { validate as validUuid } from "uuid";

export type TranslationNamespaces = string;

const host = process.env.REACT_APP_UPLOAD_CLOUDFRONT_URL;

// be warned = there's no catch handler here 
const loadNamespaceRemote = async (
	lang: string,
	ns: string,
	uuid: string,
	version?: string,
	isPublished?: boolean
): Promise<Record<string, string>> => {
	const published = isPublished ? `published/` : '';
	return await fetch(
		`${host}/translations/${published}${uuid}/${lang}/${ns}.json?v=${version}`
	)
		.then(res => {
			if (res.status === 200) {
				return res.json();
			} else {
				throw new Error(`${res.status} ${res.statusText}`);
			}
		});
};

const defaultNamespaces = ["homepage", "session", "email"];

export const translationNamespaces: TranslationNamespaces[] = [
	...defaultNamespaces
];

function convertGenerals(ns: string) {
	if (ns === 'homepage') {
		return 'general';
	}

	if (ns === 'session') {
		return 'session-general';
	}

	return ns;
}

/*

This is a custom i18next loader module. We want to defer loading in the translated
text until we know for certain what the state of the event is. This is slower in dev than it is
on the live site because the event data will already be available on page load.

*/
class StreamsLoader implements ThirdPartyModule {
	defaultNamespaces: string[] = defaultNamespaces;
	i18: typeof i18n | undefined;
	allNamespaces: Set<string> = new Set();
	languageMap: Map<string, Set<string>> = new Map();
	published?: boolean;
	type: '3rdParty' = '3rdParty' as const;
	useRemote?: boolean;
	uuid?: string;
	version?: string;
	nsTimeout?: NodeJS.Timeout;
	defaultLanguage?: string;

	init(i18: typeof i18n) {
		this.i18 = i18;
		this.i18.on('languageChanged', this.languageChange);
	}

	addNamespace = (ns: string) => {
		// don't need to re-run this if we already have this language and namespace combo
		if (this.languageMap.get(i18n.language)?.has(ns)) {
			return;
		}

		this.allNamespaces.add(ns);

		if (this.nsTimeout) {
			clearTimeout(this.nsTimeout);
		}

		this.nsTimeout = setTimeout(() => {
			this.checkLoadStatus();
		}, 150);
	};

	languageChange = () => {
		this.checkLoadStatus();
	};

	shouldGetRemotes = (ns: string) => {
		if (i18n.language === this.defaultLanguage) {
			return this.useRemote && this.uuid && this.defaultNamespaces.includes(ns);
		} else {
			return (this.useRemote && this.uuid);
		}
	};

	checkLoadStatus = () => {
		const language = i18n.language;

		if (!this.languageMap.has(i18n.language)) {
			this.languageMap.set(i18n.language, new Set());
		}

		// iterate through selected namespaces
		for (const ns of this.allNamespaces.values()) {
			this.languageMap.get(i18n.language)?.add(ns);
			if (this.i18 && !this.i18.hasResourceBundle(language, ns)) {
				if (this.shouldGetRemotes(ns)) {
					loadNamespaceRemote(language, convertGenerals(ns), this.uuid as string, this.version, this.published)
						.then(translation => {
							this.i18?.addResourceBundle(language, ns, translation, undefined, true);
							store.dispatch(setTranslationUpdates(Date.now()));
						}).catch(this.handleRemoteFailure(ns, language));
				} else if (this.defaultNamespaces.includes(ns)) {
					import(`./${language}/${ns}.json`).then((result) => {
						i18n.addResourceBundle(
							language,
							ns,
							result.default
						);
						store.dispatch(setTranslationUpdates(Date.now()));
					});
				}
			}
		}
	};

	loadStaticHome = (lang: string) => {
		import(`./${lang}/homepage.json`).then((result) => {
			i18n.addResourceBundle(
				lang,
				'homepage',
				result.default
			);
			store.dispatch(setTranslationUpdates(Date.now()));
		});
	};

	loadStaticSessions = (lang: string) => {
		import(`./${lang}/session.json`).then((result) => {
			i18n.addResourceBundle(
				lang,
				'session',
				result.default
			);
			store.dispatch(setTranslationUpdates(Date.now()));
		});
	};

	loadStaticEmails = (lang: string) => {
		import(`./${lang}/email.json`).then((result) => {
			i18n.addResourceBundle(
				lang,
				'email',
				result.default
			);
			store.dispatch(setTranslationUpdates(Date.now()));
		});
	};

	// assuming that the admin has uploaded new files, we need to refresh all remote translations within the view
	refresh = () => {
		if (!this.useRemote || !this.uuid) return;
		for (const [lang, namespaces] of Array.from(this.languageMap.entries())) {
			for (const ns of namespaces.values()) {
				this.i18?.removeResourceBundle(lang, ns);
				if (this.shouldGetRemotes(ns)) {
					loadNamespaceRemote(lang, convertGenerals(ns), this.uuid, this.version ?? new Date().toISOString(), this.published)
						.then(translation => {
							this.i18?.addResourceBundle(lang, ns, translation, undefined, true);
							store.dispatch(setTranslationUpdates(Date.now()));
						}).catch(this.handleRemoteFailure(ns, lang));
				}
			}
		}
	};

	postEventLoad = (eventBundle: BrandliveEvent & { feature_flags: FeatureFlagMap }, published = true) => {
		if (!this.i18) {
			return;
		}

		const uuidChange = this.uuid && validUuid(this.uuid) && this.uuid !== eventBundle.uuid;
		this.defaultLanguage = eventBundle.settings.i18n?.base;
		this.useRemote = eventBundle.feature_flags[FeatureFlagsEnum.translations_v2] && eventBundle.settings.translation_version === 2;
		this.version = String(eventBundle.modified_at);
		this.published = published;
		this.uuid = eventBundle.uuid;
		const lang = this.i18.language;
		this.i18.removeResourceBundle(lang, 'homepage');
		this.i18.removeResourceBundle(lang, 'session');
		this.i18.removeResourceBundle(lang, 'email');

		// the event has changed, we need to reload all resources
		// this means that the event has been changed from the admin view
		if (uuidChange && this.useRemote) {
			for (const [lang, namespaces] of this.languageMap.entries()) {
				for (const ns of namespaces.values()) {
					this.i18?.removeResourceBundle(lang, ns);
				}
			}

			this.allNamespaces = new Set(['homepage', 'session']);
			this.languageMap = new Map();
			this.languageMap.set(lang, new Set(['homepage', 'session']));
		}

		/*
	
		For the sake of speed, we're loading all these resources in parallel rather than 
		in a promise.all - we want things to become visible the moment they're loaded
	
		*/

		if (this.useRemote) {
			loadNamespaceRemote(lang, 'general', this.uuid, this.version ?? new Date().toISOString(), this.published)
				.then(translation => {
					if (Object.keys(translation).length) {
						this.i18?.addResourceBundle(lang, 'homepage', translation, undefined, true);
					} else {
						this.loadStaticHome(lang);
					}

					store.dispatch(setTranslationUpdates(Date.now()));
				}).catch(() => this.loadStaticHome(lang));

			loadNamespaceRemote(lang, 'session-general', this.uuid, this.version ?? new Date().toISOString(), this.published)
				.then(translation => {
					if (Object.keys(translation).length) {
						this.i18?.addResourceBundle(lang, 'session', translation, undefined, true);
					} else {
						this.loadStaticSessions(lang);
					}

					store.dispatch(setTranslationUpdates(Date.now()));
				}).catch(() => this.loadStaticSessions(lang));
		} else {
			this.loadStaticHome(lang);
			this.loadStaticSessions(lang);
			this.loadStaticEmails(lang);
		}
	};

	handleRemoteFailure = (ns: string, language: string) => {
		return (e: any) => {
			console.warn(`Missing ${language} translation for ${ns}, was likely not uploaded for this event.\n\n${e.toString()}`);
			if (ns === 'homepage') {
				this.loadStaticHome(language);
			} else if (ns === 'session') {
				this.loadStaticSessions(language);
			}
		};
	};
}

export const streamsLoader = new StreamsLoader();

i18n
	.use(LanguageDetector)
	.use(initReactI18next)
	.use(streamsLoader)
	.init({
		lng: "en",
		ns: translationNamespaces,
		// debug: process.env.NODE_ENV === 'production' ? false : true,
		// debug: true,
		detection: {
			order: ["queryString", "cookie"],
			caches: ["cookie"]
		},
		interpolation: {
			escapeValue: false
		},
		partialBundledLanguages: true
	});

export const tOptions: TOptions = {
	keySeparator: false,
	defaultValue: null
};
